import React, { useState, useMemo } from 'react';

// ── Stat formatting helpers ──────────────────
function formatIP(val) {
  if (val == null) return null;
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const full = Math.floor(n);
  const dec = Math.round((n - full) * 10);
  return dec === 0 ? `${full}` : `${full}.${dec}`;
}

function formatAvg(val) {
  if (val == null || isNaN(val)) return null;
  return val.toFixed(3).replace(/^0/, '');
}

function formatEra(val) {
  if (val == null || isNaN(val)) return null;
  return val.toFixed(2);
}

function formatInt(val) {
  if (val == null) return null;
  const n = Number(val);
  if (isNaN(n)) return null;
  return `${Math.round(n)}`;
}

// ── Build selectable stat chips from a game ──
function buildGameStats(game) {
  const stats = [];

  // Pitching first
  const p = game.pitching;
  if (p) {
    const ip = formatIP(p.ip);
    if (ip != null) stats.push({ key: 'ip', label: 'IP', value: ip, display: `${ip} IP` });
    if (p.k != null) stats.push({ key: 'k', label: 'K', value: formatInt(p.k), display: `${formatInt(p.k)} Ks` });
    if (p.bb != null) stats.push({ key: 'bb_p', label: 'BB', value: formatInt(p.bb), display: `${formatInt(p.bb)} BBs` });
    if (p.er != null) stats.push({ key: 'er', label: 'ER', value: formatInt(p.er), display: `${formatInt(p.er)} ER` });
    if (p.h != null) stats.push({ key: 'h_p', label: 'H allowed', value: formatInt(p.h), display: `${formatInt(p.h)} H` });
    if (p.avg_velo) stats.push({ key: 'velo', label: 'Velo', value: `${Math.round(p.avg_velo)}`, display: `${Math.round(p.avg_velo)} MPH` });
  }

  // Batting second
  const b = game.batting;
  if (b) {
    const ab = Number(b.ab || 0);
    const h = Number(b.h || 0);
    if (ab > 0) stats.push({ key: 'avg', label: 'H/AB', value: `${h}/${ab}`, display: `${h}/${ab}` });
    if (b.hr > 0) stats.push({ key: 'hr', label: 'HR', value: formatInt(b.hr), display: `${formatInt(b.hr)} HR` });
    if (b.rbi != null) stats.push({ key: 'rbi', label: 'RBI', value: formatInt(b.rbi), display: `${formatInt(b.rbi)} RBI` });
    if (b.bb > 0) stats.push({ key: 'bb_b', label: 'BB', value: formatInt(b.bb), display: `${formatInt(b.bb)} BB` });
    if (b.r > 0) stats.push({ key: 'r', label: 'R', value: formatInt(b.r), display: `${formatInt(b.r)} R` });
    if (b.k != null) stats.push({ key: 'k_b', label: 'K', value: formatInt(b.k), display: `${formatInt(b.k)} K` });
    if (b['2b'] > 0) stats.push({ key: '2b', label: '2B', value: formatInt(b['2b']), display: `${formatInt(b['2b'])} 2B` });
    if (b['3b'] > 0) stats.push({ key: '3b', label: '3B', value: formatInt(b['3b']), display: `${formatInt(b['3b'])} 3B` });
    if (b.sb > 0) stats.push({ key: 'sb', label: 'SB', value: formatInt(b.sb), display: `${formatInt(b.sb)} SB` });
  }

  return stats;
}

// ── Build selectable stat chips from season totals ──
function buildSeasonStats(games) {
  const stats = [];

  // Pitching totals
  let ipOuts = 0, er = 0, pk = 0, pbb = 0, veloVals = [];
  for (const g of games) {
    const p = g.pitching;
    if (!p) continue;
    const ipVal = parseFloat(p.ip || 0);
    const full = Math.floor(ipVal);
    const outs = Math.round((ipVal - full) * 10);
    ipOuts += full * 3 + outs;
    er += Number(p.er || 0);
    pk += Number(p.k || 0);
    pbb += Number(p.bb || 0);
    if (p.avg_velo) veloVals.push(p.avg_velo);
  }
  if (ipOuts > 0) {
    const fullInnings = Math.floor(ipOuts / 3);
    const rem = ipOuts % 3;
    const ipStr = rem === 0 ? `${fullInnings}` : `${fullInnings}.${rem}`;
    stats.push({ key: 'ip', label: 'IP', value: ipStr, display: `${ipStr} IP` });
    const ipDec = fullInnings + rem / 3;
    if (ipDec >= 3) {
      const era = (er / ipDec) * 9;
      stats.push({ key: 'era', label: 'ERA', value: formatEra(era), display: `${formatEra(era)} ERA` });
    }
    stats.push({ key: 'k', label: 'K', value: `${pk}`, display: `${pk} Ks` });
    stats.push({ key: 'bb_p', label: 'BB', value: `${pbb}`, display: `${pbb} BBs` });
    if (veloVals.length > 0) {
      const avg = veloVals.reduce((a, b) => a + b, 0) / veloVals.length;
      stats.push({ key: 'velo', label: 'Avg Velo', value: `${Math.round(avg)}`, display: `${Math.round(avg)} MPH avg` });
    }
  }

  // Batting totals
  let h = 0, ab = 0, hr = 0, rbi = 0, bb = 0, r = 0, sb = 0, b2 = 0, b3 = 0;
  for (const g of games) {
    const b = g.batting;
    if (!b) continue;
    h += Number(b.h || 0);
    ab += Number(b.ab || 0);
    hr += Number(b.hr || 0);
    rbi += Number(b.rbi || 0);
    bb += Number(b.bb || 0);
    r += Number(b.r || 0);
    sb += Number(b.sb || 0);
    b2 += Number(b['2b'] || 0);
    b3 += Number(b['3b'] || 0);
  }
  if (ab > 0) {
    const avg = h / ab;
    stats.push({ key: 'avg', label: 'AVG', value: formatAvg(avg), display: `${formatAvg(avg)} AVG` });
    stats.push({ key: 'hab', label: 'H/AB', value: `${h}/${ab}`, display: `${h}/${ab}` });
  }
  if (hr > 0) stats.push({ key: 'hr', label: 'HR', value: `${hr}`, display: `${hr} HR` });
  if (rbi > 0) stats.push({ key: 'rbi', label: 'RBI', value: `${rbi}`, display: `${rbi} RBI` });
  if (bb > 0) stats.push({ key: 'bb_b', label: 'BB', value: `${bb}`, display: `${bb} BB` });
  if (r > 0) stats.push({ key: 'r', label: 'R', value: `${r}`, display: `${r} R` });
  if (sb > 0) stats.push({ key: 'sb', label: 'SB', value: `${sb}`, display: `${sb} SB` });
  if (b2 > 0) stats.push({ key: '2b', label: '2B', value: `${b2}`, display: `${b2} 2B` });
  if (b3 > 0) stats.push({ key: '3b', label: '3B', value: `${b3}`, display: `${b3} 3B` });

  return stats;
}

const MAX_STATS = 4;

export default function StatPickerModal({ isOpen, onClose, onConfirm, games }) {
  const [source, setSource] = useState(null); // 'game' | 'season'
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const sortedGames = useMemo(() =>
    [...(games || [])].sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate)),
    [games]
  );

  const selectedGame = useMemo(() =>
    sortedGames.find(g => g.id === selectedGameId),
    [sortedGames, selectedGameId]
  );

  const availableStats = useMemo(() => {
    if (source === 'game' && selectedGame) return buildGameStats(selectedGame);
    if (source === 'season') return buildSeasonStats(games || []);
    return [];
  }, [source, selectedGame, games]);

  function toggleStat(key) {
    setSelectedKeys(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= MAX_STATS) return prev;
      return [...prev, key];
    });
  }

  function handleConfirm() {
    const ordered = availableStats.filter(s => selectedKeys.includes(s.key));
    const line = ordered.map(s => s.display).join(' · ');
    let context = null;
    if (source === 'season') {
      context = 'Season Stats';
    } else if (source === 'game' && selectedGame) {
      const raw = selectedGame.gameDate;
      const d = new Date(raw + 'T12:00:00');
      const formatted = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const opp = selectedGame.opponent || 'Unknown';
      context = `vs ${opp} · ${formatted}`;
    }
    onConfirm(line, context);
    handleClose();
  }

  function handleClose() {
    setSource(null);
    setSelectedGameId(null);
    setSelectedKeys([]);
    onClose();
  }

  function handleSourceSelect(s) {
    setSource(s);
    setSelectedGameId(null);
    setSelectedKeys([]);
  }

  if (!isOpen) return null;

  return (
    <div className="spm-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="spm-modal">
        <div className="spm-header">
          <span className="spm-title">Import Stats</span>
          <button className="spm-close" onClick={handleClose}>×</button>
        </div>

        {/* Step 1 — Source */}
        <div className="spm-source-row">
          <button
            className={`spm-source-btn${source === 'game' ? ' active' : ''}`}
            onClick={() => handleSourceSelect('game')}
          >
            Single Game
          </button>
          <button
            className={`spm-source-btn${source === 'season' ? ' active' : ''}`}
            onClick={() => handleSourceSelect('season')}
          >
            Season Totals
          </button>
        </div>

        {/* Step 2 — Game picker */}
        {source === 'game' && (
          <div className="spm-game-list">
            {sortedGames.length === 0 && (
              <p className="spm-empty">No games tracked yet.</p>
            )}
            {sortedGames.map(g => (
              <button
                key={g.id}
                className={`spm-game-row${selectedGameId === g.id ? ' active' : ''}`}
                onClick={() => { setSelectedGameId(g.id); setSelectedKeys([]); }}
              >
                <span className="spm-game-date">{g.gameDate}</span>
                <span className="spm-game-opp">vs {g.opponent || 'Unknown'}</span>
                <span className="spm-game-tags">
                  {g.pitching && <span className="spm-tag">P</span>}
                  {g.batting && <span className="spm-tag">B</span>}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Step 3 — Stat chips */}
        {availableStats.length > 0 && (
          <>
            <p className="spm-hint">Pick up to {MAX_STATS} stats ({selectedKeys.length}/{MAX_STATS} selected)</p>
            <div className="spm-chips">
              {availableStats.map(s => (
                <button
                  key={s.key}
                  className={`spm-chip${selectedKeys.includes(s.key) ? ' active' : ''}${!selectedKeys.includes(s.key) && selectedKeys.length >= MAX_STATS ? ' disabled' : ''}`}
                  onClick={() => toggleStat(s.key)}
                  disabled={!selectedKeys.includes(s.key) && selectedKeys.length >= MAX_STATS}
                >
                  {s.display}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Confirm */}
        {selectedKeys.length > 0 && (
          <button className="spm-confirm" onClick={handleConfirm}>
            Use These Stats →
          </button>
        )}
      </div>
    </div>
  );
}
